import React, { useCallback, useEffect, useState } from "react";
import DragDropfile from "../../../form/DragDropfile";
import { AnimatePresence, motion } from "framer-motion";
import TextEditor from "../../../elements/TextEditor";
import Form from "../../../form/Form";
import Input from "../../../form/Input";
import Card from "../../../elements/Card";
import Select from "../../../form/select/Select";
import Button from "../../../form/Button";
import Checkbox from "../../../form/Checkbox";
import useForm from "../../../../hooks/useForm";
import Textarea from "../../../form/Textarea";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { organizationAddEdit } from "../../../../services/organizationService";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import Editor from "../../../elements/tiptap-editor/Editor";
import LoadingFallback from "../../../elements/LoadingFallback";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LangSwitch from "../../../elements/LangSwitch";
import { countryList } from "../../../../services/countryService";
import { getCurrency } from "../../../../helpers";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useDispatch } from "react-redux";
import { setOrganizationDetails } from "../../../../redux/slice/organizationSlice";

const validation = {
  name: { required: true, message: "Name is required" },
};

const BasicSettingsManageForm = ({
  data = {}, //organization details=========>
  organizationDetailsRefetch,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { lang } = useSelector((state) => state?.langProvider);
  const [selectedLang, setSelectedLang] = useState(lang || "en");
  const [searchTimezone, setSearchTimezone] = useState(null);
  const [searchCurrency, setSearchCurrency] = useState("");
  const currency_search = useDebounce(searchCurrency, 300);

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({ validationRules: validation });

  useEffect(() => {
    if (data?._id) {
      setFieldsValue({
        name: data?.name?.[selectedLang],
        timeZone: data?.setting?.timeZone,
        description: data?.description?.[selectedLang],
        logo: { ...data?.logo, type: "image" },
        timeZoneVisibility: data?.setting?.timeZoneVisibility,
        timeoutSpeed: data?.setting?.timeoutSpeed,
        hideLogo: data?.setting?.hideLogo,
        captcha: data?.setting?.captcha,
        notificationEmail: data?.setting?.notificationEmail,
        currency: {
          ...data?.setting?.currency,
          value: data?.setting?.currency?.code,
        },
      });
    }
  }, [JSON.stringify(data), selectedLang]);

  const timezoneOptions = moment.tz.names().map((tz) => {
    const offsetMin = moment.tz(tz).utcOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const hh = String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, "0");
    const mm = String(Math.abs(offsetMin) % 60).padStart(2, "0");
    // Extract the city part from tz, e.g., “America/New_York”
    const city = tz.split("/").pop().replace(/_/g, " ");
    return {
      name: `(GMT${sign}${hh}:${mm}) ${tz}`,
      value: `(GMT${sign}${hh}:${mm}) ${tz}`,
      offset: `GMT${sign}${hh}:${mm}`,
      city,
    };
  });

  const dateFomatOptions = [
    { name: "Wed 16 Jul 2025", value: "D j M Y" },
    { name: "Wed Jul 16, 2025", value: "D M j, Y" },
  ];

  const timeformatOptions = [
    { name: "4:30 PM", value: "g:i A" },
    { name: "16:30", value: "H:i" },
  ];

  const languageOptions = [
    { name: "English", value: "en" },
    { name: "Catalan", value: "ca" },
    { name: "Chinese", value: "zh" },
    { name: "Dutch", value: "nl" },
    { name: "German", value: "de" },
    { name: "Greek", value: "el" },
    { name: "French", value: "fr" },
    { name: "Indonesian", value: "id" },
    { name: "Italian", value: "it" },
    { name: "Japanese", value: "ja" },
    { name: "Portuguese", value: "pt" },
    { name: "Romanian", value: "ro" },
    { name: "Spanish", value: "es" },
    { name: "Swedish", value: "sv" },
  ];

  const showtimezoneOptions = [
    { name: t("All events"), value: "all" },
    { name: t("Online events only"), value: "ONLINE" },
    { name: t("No events"), value: "none" },
  ];

  const basketSpeedOptions = [
    { name: t("No preference, use default settings"), value: "1" },
    {
      name: t(
        "Slow - Tickets held in baskets between 30 to 120 minutes (not recommended for fast selling events)"
      ),
      value: "2",
    },
    {
      name: t(
        "Relaxed – Tickets held in baskets between 15 to 60 minutes (not recommended for fast selling events)"
      ),
      value: "3",
    },
    { name: t("Fast – Tickets held between 7 to 30 minutes"), value: "4" },
    { name: "Faster – Tickets held between 5 to 15 minutes", value: "5" },
    {
      name: t(
        "Aggressive – Tickets held between 5 to 8 minutes (not recommended if you collect a lot of data)"
      ),
      value: "6",
    },
  ];

  const { mutate: saveOrganizationSettings, isPending } = useMutation({
    mutationFn: async (value) => {
      const payload = {
        ...value,
        name: { ...data?.name, [selectedLang]: value?.name },
        description: {
          ...data?.description,
          [selectedLang]: value?.description,
        },
        setting: {
          privacy: value?.privacy,
          timeZone: value?.timeZone,
          timeZoneVisibility: value?.timeZoneVisibility,
          dateFormat: value?.dateFormat,
          timeFormat: value?.timeFormat,
          hideLogo: value?.hideLogo,
          captcha: value?.captcha,
          notification: value?.notification,
          notificationEmail: value?.notificationEmail,
          timeoutSpeed: value?.timeoutSpeed,
          default: value?.default,
          currency: {
            code: value?.currency?.value,
            name: value?.currency?.name,
            symbol: value?.currency?.symbol,
          },
        },
        ...(data?._id && { id: data?._id }),
      };
      delete payload?.currency;
      delete payload?.privacy;
      delete payload?.timeZone;
      delete payload?.timeZoneVisibility;
      delete payload?.dateFormat;
      delete payload?.timeFormat;
      delete payload?.hideLogo;
      delete payload?.captcha;
      delete payload?.notification;
      delete payload?.notificationEmail;
      delete payload?.default;
      delete payload?.timeoutSpeed;
      const res = await organizationAddEdit(payload);
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        dispatch(setOrganizationDetails(res?.data));
        organizationDetailsRefetch();
        toast.success(res.message || "Updated Successfully!");
        // navigate(`/${lang}/user/organization/overview`);
      } else {
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const {
    data: countryData,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["country-list-event", currency_search],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await countryList({
        page: pageParam,
        limit: 10,
        groupBy: "currency",
        ...(currency_search && {
          keyword: currency_search,
        }),
      });
      return res?.docs || {};
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.metadata?.hasNextPage
        ? lastPage?.metadata?.nextPage
        : undefined;
    },
  });

  const polishedCountryList = useCallback(
    ({ list = [] }) => {
      if (
        list?.some(
          (item) =>
            item?._id === (values?.currency?.code || values?.currency?.value)
        )
      ) {
        return list;
      } else {
        if (values?.currency?.code || values?.currency?.value) {
          return [
            {
              currency_symbol: values?.currency?.symbol,
              currency_name: values?.currency?.name,
              _id: values?.currency?.code || values?.currency?.value,
            },
            ...list,
          ];
        } else {
          return list;
        }
      }
    },
    [JSON.stringify(values?.currency)]
  );

  return (
    <Form handleSubmit={handleSubmit} onSubmit={saveOrganizationSettings}>
      <div className="bg-white py-4 px-4 rounded-md">
        <div className="space-y-6">
          <Card
            title={t("Basic settings")}
            className={"py-3 md:py-auto px-3 md:px-auto"}
          >
            <div className="pb-4">
              <LangSwitch
                langs={["en", "es"]}
                selectedLang={selectedLang}
                setSelectedLang={setSelectedLang}
              />
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-4">
                <Input
                  inputName={"name"}
                  label={t("Organization name")}
                  labelClasses={
                    "!text-base md:!text-lg !text-deep-purple !mb-2"
                  }
                  inputClasses={
                    "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                  }
                  onChange={handleChange}
                  value={values?.name || ""}
                  {...formValidate(errors, "name")}
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <Select
                  label={t("Timezone")}
                  labelClasses={
                    "!text-base md:!text-lg !text-deep-purple !mb-2"
                  }
                  dropdownData={
                    searchTimezone
                      ? timezoneOptions?.filter(
                          (item) =>
                            item?.name
                              ?.toLowerCase()
                              .includes(searchTimezone?.toLowerCase()) ||
                            item?.city
                              ?.toLowerCase()
                              .includes(searchTimezone?.toLowerCase()) ||
                            item?.value
                              ?.toLowerCase()
                              .includes(searchTimezone?.toLowerCase())
                        )
                      : timezoneOptions
                  }
                  selectedValue={values?.timeZone?.value || ""}
                  onChange={(val, item) => {
                    handleChange({
                      target: { name: "timeZone", value: item },
                    });
                  }}
                  isSearch
                  onSearchHandler={setSearchTimezone}
                  placeholder="Select a timezone"
                  dropdownClass="w-full"
                  dropdownButtonClass={
                    "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                  }
                  optionsClass="hover:bg-primary/10"
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <Select
                  label={t("Currency")}
                  labelClasses={
                    "!text-base md:!text-lg !text-deep-purple !mb-2"
                  }
                  selectedValue={values?.currency?.value}
                  dropdownData={
                    polishedCountryList({
                      list: countryData?.pages?.flatMap((page) => page?.data),
                    })?.map((c) => {
                      return {
                        ...c,
                        name: (
                          <div className="flex items-center gap-2">
                            <div className="h-3 min-w-3 flex items-center justify-center bg-slate-100 rounded-md p-2 py-3">
                              {c?.currency_symbol}
                            </div>{" "}
                            {c?.currency_name} {`(${c?._id})`}
                          </div>
                        ),
                        value: c?._id,
                      };
                    }) || [{ name: "Select currency", value: "" }]
                  }
                  placeholder="Select a currency"
                  dropdownClass="w-full"
                  dropdownButtonClass={
                    "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                  }
                  optionsClass="hover:bg-primary/10"
                  onChange={(_, item) => {
                    delete item?.name;
                    handleChange({
                      target: {
                        value: {
                          ...item,
                          name: item?.currency_name,
                          symbol: item?.currency_symbol,
                        },
                        name: "currency",
                      },
                    });
                  }}
                  isSearch
                  searchValue={searchCurrency}
                  onSearchHandler={setSearchCurrency}
                  onScroll={(event) => {
                    if (
                      Math.ceil(event.target.scrollTop) + 70 >=
                        event.target.scrollHeight - event.target.clientHeight &&
                      hasNextPage
                    ) {
                      fetchNextPage();
                    }
                  }}
                />
              </div>
              <div className="col-span-12">
                <Editor
                  labelClasses={
                    "!text-base md:!text-lg !text-deep-purple !mb-2"
                  }
                  label={t("Description")}
                  height={200}
                  value={values?.description || ""}
                  onChange={(val) => {
                    handleChange({
                      target: { name: "description", value: val },
                    });
                  }}
                />
              </div>
              <div className="col-span-12">
                <DragDropfile
                  label={t("Default organization logo")}
                  labelClasses={
                    "!text-base md:!text-lg !text-deep-purple !mb-2 capitalize"
                  }
                  formats={".jpg, .png, .jpeg"}
                  isMultiple
                  value={values?.logo}
                  handleFile={(file) => {
                    handleChange({
                      target: { name: "logo", value: file[0] },
                    });
                  }}
                />
              </div>
              {/* <div className="col-span-12 md:col-span-6">
                <Select
                  label="Date format"
                  labelClasses={"!text-base md:!text-lg !text-deep-purple !mb-2"}
                  dropdownData={dateFomatOptions}
                  selectedValue={selectedDateFormat}
                  onChange={handleDateFormatChange}
                  dropdownClass="w-full"
                  dropdownButtonClass={
                    "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                  }
                  optionsClass="hover:bg-primary/10"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Select
                  label="Time format"
                  labelClasses={"!text-base md:!text-lg !text-deep-purple !mb-2"}
                  dropdownData={timeformatOptions}
                  selectedValue={selectedTimeformat}
                  onChange={handleTimeformatChange}
                  dropdownClass="w-full"
                  dropdownButtonClass={
                    "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                  }
                  optionsClass="hover:bg-primary/10"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Select
                  label="Language"
                  labelClasses={"!text-base md:!text-lg !text-deep-purple !mb-2"}
                  dropdownData={languageOptions}
                  selectedValue={selectedLanguage}
                  onChange={handleLanguageChange}
                  dropdownClass="w-full"
                  dropdownButtonClass={
                    "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                  }
                  optionsClass="hover:bg-primary/10"
                />
              </div> */}
              <div className="col-span-12 md:col-span-6">
                <Select
                  label={t("Show timezone")}
                  labelClasses={
                    "!text-base md:!text-lg !text-deep-purple !mb-2"
                  }
                  dropdownData={showtimezoneOptions}
                  selectedValue={values?.timeZoneVisibility || ""}
                  onChange={(val) =>
                    handleChange({
                      target: { name: "timeZoneVisibility", value: val },
                    })
                  }
                  dropdownClass="w-full"
                  dropdownButtonClass={
                    "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                  }
                  optionsClass="hover:bg-primary/10"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Select
                  label={t("Basket timeout speed")}
                  labelClasses={
                    "!text-base md:!text-lg !text-deep-purple !mb-2"
                  }
                  dropdownData={basketSpeedOptions}
                  selectedValue={values?.timeoutSpeed || ""}
                  dropdownClass="w-full"
                  dropdownButtonClass={
                    "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                  }
                  optionsClass="hover:bg-primary/10"
                  onChange={(val) =>
                    handleChange({
                      target: { name: "timeoutSpeed", value: val },
                    })
                  }
                />
              </div>
              <div className="col-span-12">
                <Checkbox
                  checkboxLabel={t("Hide Organization logo")}
                  checkboxLableClass={
                    "font-medium text-text-disabled text-base md:text-lg text-deep-purple"
                  }
                  checked={values?.hideLogo || ""}
                  onChange={handleChange}
                  checkboxName="hideLogo"
                />
              </div>
              <div className="col-span-12">
                <Checkbox
                  checkboxLabel={t(
                    "Always show a CAPTCHA challenge when payment details aren't required"
                  )}
                  checkboxLableClass={
                    "font-medium text-text-disabled text-base md:text-lg text-deep-purple"
                  }
                  checked={values?.captcha || ""}
                  onChange={handleChange}
                  checkboxName="captcha"
                />
              </div>
              <div className="col-span-12">
                <Checkbox
                  checkboxLabel={t("Send new order notifications by email")}
                  checkboxLableClass={
                    "font-medium text-text-disabled text-base md:text-lg text-deep-purple"
                  }
                  checked={values?.notificationEmail || ""}
                  onChange={handleChange}
                  checkboxName="notificationEmail"
                />
              </div>
              {/* <div className="col-span-12">
                <div className="text-text-disabled text-xl font-bold">
                  Who should receive notifications?
                </div>
                <Checkbox
                  checkboxLabel="Account owner"
                  checkboxLableClass={"font-mediumtext-base md:text-lg text-deep-purple"}
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  checkboxName="terms"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <AnimatePresence initial={false}>
                  {emailInputs.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="flex items-center gap-1 mb-2">
                        <div className="flex-shrink w-full">
                          <Input
                            inputPlaceholder="Enter email"
                            inputClasses={
                              "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                            }
                          />
                        </div>
                        <div className="flex-shrink-0">
                          <Button
                            buttonIcon="fa-regular fa-xmark"
                            buttonClasses="px-0 bg-transparent text-primary"
                            buttonFunction={() => removeEmailInput(item.id)}
                          />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {emailInputs.length < 3 && (
                  <Button
                    buttonLabel="Add Email"
                    buttonLabelClasses="text-deep-purple"
                    buttonClasses="bg-transparent border border-gray-200"
                    buttonFunction={addEmailInput}
                  />
                )}
              </div> */}
            </div>
          </Card>
        </div>
        <div className="flex mt-7 gap-3 justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2] pr-6">
          <Button
            {...(isPending && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
            buttonType="submit"
            buttonLabel={t("Save")}
          />
        </div>
      </div>
    </Form>
  );
};

export default BasicSettingsManageForm;
